import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { CreateFamilyMedicalHistoryBody } from '@/types/patient-chart-api';

/**
 * POST /api/Patients/CreateFamilyMedicalHistory
 *
 * Wire body curl-verified by Andrew 2026-05-20. See
 * `CreateFamilyMedicalHistoryBody` type doc for the Family-History-specific
 * gotchas (most notably: `outCome` typo preserved, `fullName: "n/a"` /
 * `dataAbsentReason: "n/a"` literals, `age: 0` sentinel for unset).
 *
 * Cache: invalidates `['patient-family-history', patientId]`. SignalR
 * `UpdatePatientChat` "FamilyHistory" event also fires for concurrent
 * sessions.
 */

interface CreateFamilyMedicalHistoryParams {
  patientId: string;
  body: CreateFamilyMedicalHistoryBody;
}

export function useCreateFamilyMedicalHistory() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateFamilyMedicalHistoryParams>({
    mutationFn: ({ body }) =>
      post<string>('/api/Patients/CreateFamilyMedicalHistory', body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-family-history', patientId]);
    },
  });
}
